import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Heading,
  Spinner,
  Table,
  TableCaption,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import {
  getRoofTypeList,
  RoofTypeModel,
  deleteRoofType,
} from "../../clients/BackOfficeApi";
import { Link } from "react-router-dom";
import { CreateRoofTypeModal } from "../../components/modals/CreateRoofTypeModal";
import { ActionButton } from "../../elements/ActionButton";
import { PaginationForm } from "../../elements/PaginationForm";
import { TableLoadingRow } from "../../elements/TableLoadingRow";

export const RoofTypeListPage = withPagination(
  { settingsKey: "roofTypes" },
  () => {
    const [roofTypes, setRoofTypes] = useState<RoofTypeModel[]>([]);
    const isAuthenticated = useIsAuthenticated();
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const { limit, offset } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);
    const [tableSpan, setTableSpan] = useState(1);

    const fetchRoofTypes = useCallback(
      async (signal?: AbortSignal, refetchAll = false) => {
        if (!isAuthenticated) return;

        setLoading(true);

        const query = refetchAll
          ? { Offset: 0, Limit: offset + limit }
          : { Offset: offset, Limit: limit };

        getRoofTypeList({
          query: {
            Offset: refetchAll ? 0 : offset,
            Limit: refetchAll ? offset + limit : limit,
          },
          signal,
          throwOnError: true,
        })
          .then(({ data }) => {
            if (signal?.aborted) return;

            const items = data?.items ?? [];

            const loadedItems = items.length;
            setLoadedOffset(query.Offset + loadedItems);
            setRoofTypes((prev) => {
              prev.splice(query.Offset, loadedItems, ...items);
              return prev.slice(0, query.Offset + loadedItems);
            });
            setTotal(data?.totalCount ?? 0);
          })
          .catch((e) => {
            if (signal?.aborted) return;

            throw e;
          })
          .finally(() => setLoading(false));
      },
      [offset, limit, isAuthenticated],
    );

    const moreRowsAvailable = useMemo(
      () => loadedOffset < total,
      [loadedOffset, total],
    );

    useEffect(() => {
      const abortController = new AbortController();

      fetchRoofTypes(abortController.signal);

      return () => abortController.abort("Component unmounted or re-rendered");
    }, [isAuthenticated, fetchRoofTypes]);

    const fetchDeleteRoofType = useCallback(
      (roofTypeId: number) => {
        deleteRoofType({
          path: { roofTypeId },
          throwOnError: true,
        }).then(() => fetchRoofTypes(undefined, true));
      },
      [fetchRoofTypes],
    );

    return (
      <>
        <Heading as="h1" mb="4">
          Daktypes
        </Heading>
        <CreateRoofTypeModal />
        <Table variant="striped" w="auto">
          <TableCaption placement="top" whiteSpace="nowrap">
            Daktypes ({total})
          </TableCaption>
          <Thead>
            <Tr ref={(el) => setTableSpan(el?.childElementCount ?? 1)}>
              <Th>Id</Th>
              <Th>Naam</Th>
              <Th>Acties</Th>
            </Tr>
          </Thead>
          <Tbody>
            <TableLoadingRow colSpan={tableSpan} loading={loading && !moreRowsAvailable} />
            {!roofTypes.length && !loading && (
              <Tr>
                <Td colSpan={tableSpan}>Geen daktypes gevonden</Td>
              </Tr>
            )}
            {roofTypes.map((roofType) => (
              <Tr key={roofType.id}>
                <Td>{roofType.id}</Td>
                <Td>{roofType.name}</Td>
                <Td>
                  <Flex direction="column" gap={2} minW={24}>
                    <Button
                      as={Link}
                      color="blue.500"
                      leftIcon={<EditIcon />}
                      mr={2}
                      to={roofType.id.toString()}
                      variant="link"
                    >
                      Bewerken
                    </Button>
                    <ActionButton
                      color="red.500"
                      confirmDescription="Weet je zeker dat je dit daktype wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden."
                      confirmTitle="Verwijderen bevestigen"
                      icon={<DeleteIcon />}
                      isDestructive
                      onClick={(confirmed: boolean) => {
                        if (!confirmed) return;
                        fetchDeleteRoofType(roofType.id);
                      }}
                      title="Verwijderen"
                      variant="link"
                    />
                  </Flex>
                </Td>
              </Tr>
            ))}
            {moreRowsAvailable && (
              <Tr>
                <Td colSpan={tableSpan}>
                  <PaginationForm loading={loading} offset={loadedOffset} />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </>
    );
  },
);
